import { z } from "zod";
import { IdSchema, PrincipalSchema, ScopeSchema, TimestampSchema } from "./primitives.ts";

/**
 * How an overlap was settled.
 * - `split`: both claims are narrowed so they no longer collide.
 * - `sequence`: one session lands first; the others wait and rebase on it. Landing is enforced in order.
 * - `handoff`: one session takes both pieces; the other releases its claim.
 * - `shared`: both proceed with shared claims, and each is notified whenever the other pushes.
 * - `escalated`: the agents could not agree; a human decides.
 */
export const ResolutionOutcomeSchema = z.enum(["split", "sequence", "handoff", "shared", "escalated"]);
/** See {@link ResolutionOutcomeSchema}. */
export type ResolutionOutcome = z.infer<typeof ResolutionOutcomeSchema>;

/** Terms of a `split`: the new scope each session's claim is narrowed to. */
export const SplitTermsSchema = z.object({
  /** One sentence describing the split. */
  summary: z.string().min(1),
  /** The replacement scope for each session's claim in the overlap. The new scopes must not collide. */
  scopes: z.array(z.object({ sessionId: IdSchema, scope: ScopeSchema })).min(1).max(2),
});
/** See {@link SplitTermsSchema}. */
export type SplitTerms = z.infer<typeof SplitTermsSchema>;

/** Terms of a `sequence`: who lands first. */
export const SequenceTermsSchema = z.object({
  /** One sentence describing the order and why. */
  summary: z.string().min(1),
  /** Both sessions of the overlap, first to land first. A later session cannot land until the earlier ones have. */
  order: z.array(IdSchema).length(2),
});
/** See {@link SequenceTermsSchema}. */
export type SequenceTerms = z.infer<typeof SequenceTermsSchema>;

/** Terms of a `handoff`: who gives the work to whom. */
export const HandoffTermsSchema = z.object({
  /** One sentence describing what moves. */
  summary: z.string().min(1),
  /** The session giving up its claim. Its claim in the overlap is released. */
  from: IdSchema,
  /** The session taking on the work. */
  to: IdSchema,
});
/** See {@link HandoffTermsSchema}. */
export type HandoffTerms = z.infer<typeof HandoffTermsSchema>;

/** Terms of a `shared` outcome: both claims become `shared`. */
export const SharedTermsSchema = z.object({
  /** One sentence describing how the two will coexist, for example who owns which part of the file. */
  summary: z.string().min(1),
});
/** See {@link SharedTermsSchema}. */
export type SharedTerms = z.infer<typeof SharedTermsSchema>;

/** Terms of an `escalated` outcome. */
export const EscalatedTermsSchema = z.object({
  /** One sentence describing the deadlock. */
  summary: z.string().min(1),
  /** What each side wants, so a human can decide without reading the whole thread. */
  positions: z.array(z.object({ sessionId: IdSchema, position: z.string().min(1) })),
});
/** See {@link EscalatedTermsSchema}. */
export type EscalatedTerms = z.infer<typeof EscalatedTermsSchema>;

/**
 * A proposed way to settle an overlap: an outcome plus its terms. Carried by proposal and counter
 * messages, and accepted as-is into a {@link Resolution}. Escalation is never proposed; it is recorded.
 */
export const ProposalSchema = z.discriminatedUnion("outcome", [
  z.object({ outcome: z.literal("split"), terms: SplitTermsSchema }),
  z.object({ outcome: z.literal("sequence"), terms: SequenceTermsSchema }),
  z.object({ outcome: z.literal("handoff"), terms: HandoffTermsSchema }),
  z.object({ outcome: z.literal("shared"), terms: SharedTermsSchema }),
]);
/** See {@link ProposalSchema}. */
export type Proposal = z.infer<typeof ProposalSchema>;

/**
 * How a resolution came about.
 * - `agreement`: both sessions agreed (a proposal was accepted). `agreedBy` lists both.
 * - `yield`: one session conceded unilaterally (sequence or handoff in the other's favour). `agreedBy` lists it.
 * - `timeout`: no reply in time, so the protocol applied its default. `agreedBy` lists a `system:` principal.
 * - `rule`: a coordinator rule decided, for example the migration lane. `agreedBy` lists a `system:` principal.
 * - `human`: a human decided, usually after escalation. `agreedBy` lists a `human:` principal.
 */
export const ResolutionViaSchema = z.enum(["agreement", "yield", "timeout", "rule", "human"]);
/** See {@link ResolutionViaSchema}. */
export type ResolutionVia = z.infer<typeof ResolutionViaSchema>;

const resolutionInputBase = {
  /** The overlap this settles. An overlap can have more than one resolution (escalated, then decided). */
  overlapId: IdSchema,
  /** Who agreed, see {@link ResolutionViaSchema}. */
  agreedBy: z.array(PrincipalSchema).min(1),
  /** How it came about. */
  via: ResolutionViaSchema,
  /** Why this outcome, in a sentence or two. Every resolution records its reason. */
  reason: z.string().min(1),
};

const resolutionBase = {
  /** Coordinator-assigned id. */
  id: IdSchema,
  ...resolutionInputBase,
  /** When it was recorded. */
  ts: TimestampSchema,
};

/**
 * The recorded settlement of an overlap, with its terms. The coordinator enforces it:
 * split narrows claims, sequence gates landing order, handoff releases a claim, shared relaxes modes.
 */
export const ResolutionSchema = z.discriminatedUnion("outcome", [
  z.object({ ...resolutionBase, outcome: z.literal("split"), terms: SplitTermsSchema }),
  z.object({ ...resolutionBase, outcome: z.literal("sequence"), terms: SequenceTermsSchema }),
  z.object({ ...resolutionBase, outcome: z.literal("handoff"), terms: HandoffTermsSchema }),
  z.object({ ...resolutionBase, outcome: z.literal("shared"), terms: SharedTermsSchema }),
  z.object({ ...resolutionBase, outcome: z.literal("escalated"), terms: EscalatedTermsSchema }),
]);
/** See {@link ResolutionSchema}. */
export type Resolution = z.infer<typeof ResolutionSchema>;

/** A {@link Resolution} without the coordinator-assigned `id` and `ts`: the input to `recordResolution`. */
export const ResolutionInputSchema = z.discriminatedUnion("outcome", [
  z.object({ ...resolutionInputBase, outcome: z.literal("split"), terms: SplitTermsSchema }),
  z.object({ ...resolutionInputBase, outcome: z.literal("sequence"), terms: SequenceTermsSchema }),
  z.object({ ...resolutionInputBase, outcome: z.literal("handoff"), terms: HandoffTermsSchema }),
  z.object({ ...resolutionInputBase, outcome: z.literal("shared"), terms: SharedTermsSchema }),
  z.object({ ...resolutionInputBase, outcome: z.literal("escalated"), terms: EscalatedTermsSchema }),
]);
/** See {@link ResolutionInputSchema}. */
export type ResolutionInput = z.infer<typeof ResolutionInputSchema>;
